import { Link, useParams } from 'react-router-dom'

import type { CompetitionDetail } from '@/api/competitionDetail'
import type { DataMeta } from '@/api/types'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock, type FormatOptions } from '@/design'
import { useMoney } from '@/features/overview/hooks'
import type { GlobalFilters } from '@/lib/filters'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { DetailTabs, TabPanel } from './DetailTabs'
import { competitionDetailErrorMessage, isNotFound } from './errors'
import { DetailHeader } from './Header'
import { useBackToListHref, useDetailTab, type DetailTab } from './params'
import { useCompetitionDetail } from './queries'
import { AdvertisingTab } from './tabs/AdvertisingTab'
import { CustomersTab } from './tabs/CustomersTab'
import { DevicesTab } from './tabs/DevicesTab'
import { FunnelTab } from './tabs/FunnelTab'
import { SalesTab } from './tabs/SalesTab'
import { SourcesTab } from './tabs/SourcesTab'
import { SummaryTab } from './tabs/SummaryTab'
import { TimelineTab } from './tabs/TimelineTab'
import '../list/list.css'
import '../competitions/competitions.css'
import './competitionDetail.css'

/**
 * Analytics › Competitions › one competition (blueprint 10 §10.7): its header, then the
 * Summary, Sales, Funnel, Marketing sources, Advertising, Customers, Devices and
 * Timeline tabs, every one under the filter bar's period, environments and website, the
 * competition implied. The main route (`GET /analytics/competitions/{id}`) is the first
 * paint and feeds the header, Summary, Sales, Customers and Timeline; Funnel, Marketing
 * sources, Devices and Advertising each read their own route when their tab opens.
 */
export function CompetitionDetailPage({ route }: { route: RouteDef }) {
  const { id = '' } = useParams()
  const { filters } = useGlobalFilters()
  const read = useCompetitionDetail(id, filters)
  const backHref = useBackToListHref(filters)
  usePageFreshness(read.data?.meta)

  const back = (
    <Link className="ci-button" to={backHref}>
      All competitions
    </Link>
  )

  if (read.isPending) {
    return (
      <SectionFrame route={route} actions={back}>
        <LoadingBlock rows={8} label="Loading competition" />
      </SectionFrame>
    )
  }

  if (!read.data) {
    return (
      <SectionFrame route={route} actions={back}>
        <Card>
          {isNotFound(read.error) ? (
            <EmptyState
              variant="no-results"
              announce
              title="Competition not found"
              description="No competition with this address is in your organisation's scope: it may not have synced yet, or it may be on another website than the one chosen in the filter bar."
              action={
                <Link className="ci-button" to={backHref}>
                  Back to all competitions
                </Link>
              }
            />
          ) : (
            <EmptyState variant="outage" announce description={competitionDetailErrorMessage(read.error)} />
          )}
        </Card>
      </SectionFrame>
    )
  }

  const { data: detail, meta } = read.data
  return (
    <SectionFrame route={route} title={detail.summary.name} description={periodLine(meta)} actions={back}>
      <Loaded competitionId={id} detail={detail} filters={filters} busy={read.isFetching} />
    </SectionFrame>
  )
}

function periodLine(meta: DataMeta): string | undefined {
  return meta.period ? `${meta.period.label} · every tab covers this period` : undefined
}

function Loaded({
  competitionId,
  detail,
  filters,
  busy,
}: {
  competitionId: string
  detail: CompetitionDetail
  filters: GlobalFilters
  busy: boolean
}) {
  const [tab, setTab] = useDetailTab()
  const money = useMoney(detail.currency)
  return (
    <div className="cd-page">
      <DetailHeader detail={detail} locale={money.locale} />
      <Card busy={busy}>
        <DetailTabs active={tab} onSelect={setTab} />
        <TabPanel tab={tab}>
          <Panel tab={tab} competitionId={competitionId} detail={detail} filters={filters} money={money} />
        </TabPanel>
      </Card>
    </div>
  )
}

/** The open tab's content. Only this one is mounted, so a tab route is read only while its tab is open. */
function Panel({
  tab,
  competitionId,
  detail,
  filters,
  money,
}: {
  tab: DetailTab
  competitionId: string
  detail: CompetitionDetail
  filters: GlobalFilters
  money: FormatOptions
}) {
  switch (tab) {
    case 'summary':
      return <SummaryTab detail={detail} locale={money.locale} />
    case 'sales':
      return <SalesTab competitionId={competitionId} daily={detail} filters={filters} money={money} />
    case 'funnel':
      return <FunnelTab competitionId={competitionId} filters={filters} />
    case 'sources':
      return <SourcesTab competitionId={competitionId} filters={filters} money={money} />
    case 'advertising':
      return <AdvertisingTab competitionId={competitionId} filters={filters} money={money} />
    case 'customers':
      return <CustomersTab customers={detail.customers} money={money} />
    case 'devices':
      return <DevicesTab competitionId={competitionId} filters={filters} />
    case 'timeline':
      return <TimelineTab detail={detail} filters={filters} locale={money.locale} />
  }
}
